import type { AxiosError, AxiosInstance } from 'axios';
import axios from 'axios';
import { useEffect, useMemo } from 'react';

import { appConfig } from '#/shared/config/AppConfig';

import { useAccessToken } from './useAccessToken';
import { useMonitoring } from './useMonitoring';

export enum MediaTypes {
  JSON = 'application/json',
}

export enum ContentTypes {
  JSON = 'application/json',
  FORM_DATA = 'multipart/form-data',
}

/**
 * A React hook that configures Axios to include an Authorization header
 * with a Bearer token in each request when the user is authenticated.
 *
 * @returns {AxiosInstance} A configured Axios instance.
 */
export const useAxiosConfig = (
  mediaType: MediaTypes = MediaTypes.JSON,
  contentType: ContentTypes = ContentTypes.JSON,
): AxiosInstance => {
  const apiVersion = appConfig.apiVersion;

  /**
   * Creates a new Axios instance with a base URL taken from configuration.
   */
  const api = useMemo(
    () =>
      axios.create({
        baseURL: appConfig.apiBaseEndpoint,
        headers: {
          Accept: `${mediaType};v=${apiVersion}`,
          'Content-Type': contentType,
        },
      }),
    [contentType, mediaType, apiVersion],
  );

  const { noticeError } = useMonitoring();
  const token = useAccessToken();

  useEffect(() => {
    const credentials = {
      accessToken: token,
    };
    // Register an Axios request interceptor to add the Authorization header
    const requestInterceptor = api.interceptors.request.use(
      async axConfig => {
        const controller = new AbortController();

        try {
          axConfig.headers.Authorization = `Bearer ${credentials?.accessToken}`;
        } catch (error) {
          const errorInfo: Record<string, unknown> = { originalError: error };

          if (error instanceof Error) {
            errorInfo.originalMessage = error.message;
          }
          noticeError(new Error('Failed to add Authorization header to request'), errorInfo);
        }

        return {
          ...axConfig,
          signal: controller.signal,
        };
      },
      (error: Error) => {
        // Forward any errors to be handled downstream
        return Promise.reject(error);
      },
    );

    // Register an Axios response interceptor to handle 401 responses
    const handleResponseError = async (error: AxiosError): Promise<never> => {
      return Promise.reject(error);
    };

    const responseInterceptor = api.interceptors.response.use(response => {
      return response;
    }, handleResponseError);

    return () => {
      // Eject interceptors when the component using this hook unmounts
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [api.interceptors.request, api.interceptors.response, noticeError, token]);

  // Return the configured Axios instance
  return api;
};
